import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { DiscordIcon } from './DiscordIcon';
import { DISCORD_URL } from '../data/horizon';
const DELAY_MS = 5000;
const SCROLL_TRIGGER = 0.35;
const EASE = [0.23, 1, 0.32, 1] as const;
export function DiscordPopup() {
  const [open, setOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const d = (s: number) => reduceMotion ? 0 : s;
  useEffect(() => {
    let shown = false;
    let timer = 0;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > SCROLL_TRIGGER) show();
    };
    const stop = () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
    function show() {
      if (shown) return;
      shown = true;
      stop();
      setOpen(true);
    }
    timer = window.setTimeout(show, DELAY_MS);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return stop;
  }, []);
  const dismiss = () => setOpen(false);
  return createPortal(<AnimatePresence data-id="element-278" data-mp="3tqb6ly8eq8rl">
      {open && <motion.div role="dialog" aria-labelledby="discord-popup-title" initial={{
      opacity: 0,
      y: 24,
      scale: 0.96
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.96
    }} transition={{
      duration: d(0.28),
      ease: EASE
    }} className="fixed inset-x-3 bottom-20 z-[65] overflow-hidden rounded-2xl border border-[#5865F2]/40 bg-[#0A1628]/95 p-3.5 shadow-[0_16px_40px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[360px] sm:rounded-3xl sm:p-5" data-id="element-279" data-mp="3tp9lt21la7wo">
          <button type="button" onClick={dismiss} aria-label="Cerrar" className="absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-white/10 hover:text-white sm:right-2 sm:top-2 sm:h-10 sm:w-10 sm:bg-white/5" data-id="element-280" data-mp="2i3b37rerqz6b">
            <X className="h-4 w-4" data-id="element-281" data-mp="3lfryns9czw94" />
          </button>
          <div className="flex items-start gap-3 pr-7 sm:gap-4 sm:pr-8" data-id="element-282" data-mp="38vkymp7ypy2">
            <motion.span initial={{
          rotate: -12,
          scale: 0.9
        }} animate={{
          rotate: 0,
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 400,
          damping: 14,
          delay: d(0.1)
        }} className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#5865F2] text-white sm:h-12 sm:w-12 sm:rounded-2xl" data-id="element-283" data-mp="3udlsmwjnidt4">
              <DiscordIcon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-284" data-mp="hwovhszg729s" />
              <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5" aria-hidden="true" data-id="element-285" data-mp="hxi8hbp02o0d">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#3BA55C] opacity-60 motion-reduce:animate-none" data-id="element-286" data-mp="vwznizchumwj" />
                <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-[#0A1628] bg-[#3BA55C]" data-id="element-287" data-mp="1ajgaxbig5yss" />
              </span>
            </motion.span>
            <motion.div initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: d(0.24),
          delay: d(0.08),
          ease: EASE
        }} data-id="element-288" data-mp="1fg70cpg566qv">
              <p id="discord-popup-title" className="font-cinzel text-sm font-bold leading-snug text-white sm:text-lg" data-id="element-289" data-mp="15m6kptn8hj3w">
                En Vanguard, todo pasa primero en Discord.
              </p>
              <p className="mt-0.5 text-xs leading-snug text-gray-300 sm:mt-1.5 sm:text-sm sm:leading-relaxed" data-id="element-290" data-mp="wli9t7t4zyv8">
                Eventos, anuncios y premios antes que nadie. No te enteres tarde.
              </p>
            </motion.div>
          </div>
          <motion.div initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: d(0.24),
        delay: d(0.14),
        ease: EASE
      }} className="mt-3 flex items-center gap-2 sm:mt-4" data-id="element-291" data-mp="8tl0bnd5tixa">
            <motion.a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" onClick={dismiss} whileTap={{
          scale: 0.97
        }} className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-full bg-[#5865F2] px-3 font-cinzel text-xs font-bold sm:h-11 sm:px-4 sm:text-sm text-white whitespace-nowrap transition-colors duration-150 hover:bg-[#4752C4]" data-id="element-292" data-mp="5u2mr9j8zk6h">
              <DiscordIcon className="h-4 w-4" data-id="element-293" data-mp="3e6gn0i865gkj" />
              Unirme ahora
            </motion.a>
            <button type="button" onClick={dismiss} className="h-9 rounded-full px-3 text-xs font-semibold sm:h-11 sm:px-4 sm:text-sm text-gray-400 whitespace-nowrap transition-colors duration-150 hover:bg-white/5 hover:text-white" data-id="element-294" data-mp="399gsn4kom299">
              Ahora no
            </button>
          </motion.div>
        </motion.div>}
    </AnimatePresence>, document.body);
}